import React from "react";
import Header from "./Header";
import FloatingRecorderFAB from "./FloatingRecorderFAB";
import DraftAutoPublisher from "./DraftAutoPublisher";
import InstallPromptDialog from "./InstallPromptDialog";
import CapacitorDeepLinkHandler from "./CapacitorDeepLinkHandler";
import CapgoBootstrap from "./CapgoBootstrap";
import FirstLoginShareToast from "./FirstLoginShareToast";
import ReferralThanksToast from "./ReferralThanksToast";
import ReferralWelcomeToast from "./ReferralWelcomeToast";
import XAttributionBeacon from "../landing/XAttributionBeacon";
import SignupGate from "../auth/SignupGate";
import TwoFactorChallengeDialog from "./TwoFactorChallengeDialog";
import ErrorBoundary from "./ErrorBoundary";

/**
 * AppShell — the persistent overlay chrome that wraps every route.
 *
 * Iter-56 refactor: previously these components were inlined into
 * App.js around the <Routes> block, which made the App component a
 * 100-line list of route definitions tangled with overlay imports.
 * Consolidating into a single shell file:
 *   1. Keeps App.js focused on routing + providers only.
 *   2. Makes it obvious which components are PERSISTENT (cross-route)
 *      vs PAGE-LEVEL — anything mounted here renders on EVERY route,
 *      including the `/auth/x/callback` and `/qr-claim/:id` deep-link
 *      handlers that should NOT have the FAB / install prompt visible.
 *      → We use route awareness (via `<Outlet />` patterns or
 *        `useLocation` inside each child) when a specific overlay
 *        wants to hide itself on certain routes.
 *   3. Single import line in App.js instead of seven.
 *
 * The wrapper deliberately renders `children` (= the <Routes/>) BEFORE
 * the overlays. This mirrors the old App.js layout and matters for
 * focus management: the page's primary content gets the tab order,
 * the overlays take over only when explicitly opened.
 */
const AppShell = ({ children }) => {
  return (
    <ErrorBoundary scope="app">
      <CapacitorDeepLinkHandler />
      <CapgoBootstrap />
      <Header />
      {/* Inner page-scope boundary so a single-route crash (e.g. the
          /map black-screen reported in v1.0 build 1) renders an
          in-place error card WITHOUT taking the Header down with it.
          Users can still tap the hamburger and navigate away. */}
      <ErrorBoundary scope="page">
        {children}
      </ErrorBoundary>
      <FloatingRecorderFAB />
      <DraftAutoPublisher />
      <InstallPromptDialog />
      <FirstLoginShareToast />
      <ReferralThanksToast />
      <ReferralWelcomeToast />
      <XAttributionBeacon />
      <SignupGate />
      <TwoFactorChallengeDialog />
    </ErrorBoundary>
  );
};

export default AppShell;
